import { EM_DASH } from '../lib/typography';
import { Component } from 'preact';
import { motion } from 'framer-motion';
import {
  abandonInstance as apiAbandonInstance,
  leaveInstance as apiLeaveInstance,
  declineInstanceInvite,
} from '../utils/arena/instancesApi';
import { resolveDiscardLeave } from '../utils/arena/discardLeave';
import { declineDraftEvent, dismissDraftMatchInvite, keystoneCostForMode, leaveDraftEvent } from '../utils/arena/draftApi';
import {
  openInstance, acceptInstanceInvite, acceptDraftInvite, acceptDraftMatchInvite,
} from '../utils/arena/instanceQueueBus';
import {
  subscribe, getState, refreshMatches, refreshActivity, markAllRead,
  removeInviteLocally, openNotification, resolveNotification,
} from '../utils/notificationsStore';
import { describeNotification, notificationHasTarget } from '../utils/notificationCopy';
import { acceptFriendRequest, declineFriendRequest } from '../utils/friendsApi';
import { refreshFriendList } from '../utils/presenceManager';
import { askConfirm } from '../utils/confirmBus';
import { GAME_MODES, stakesLabel } from '../utils/arena/gameModes';
import { UI } from '../utils/arena/uiSounds';
import { timeAgo } from '../utils/arena/timeFormat';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { getSorceryCards } from '../utils/sorcery/cardDb';
import { getCachedFriendList } from '../utils/presenceManager';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, COIN_COLOR,
  GOLD_BTN, TEXTURED_PANEL_STYLE, CONTENT_BG_DEFAULT, FourCorners,
} from '../lib/medievalTheme';
import { CoinIcon, KeystoneIcon, IconChevronDown } from './ui/icons';
import { toast } from 'sonner';
import { k, t } from '../localization/t';

// Lobby leashes, mirrored from the server sweeper so the row can show
// "closes in 2h 14m". Public lobbies are open challenges and die at 3h;
// anything else gets a day.
const PUBLIC_LOBBY_TTL_MS = 3 * 60 * 60 * 1000;
const LOBBY_TTL_MS = 24 * 60 * 60 * 1000;

function modeTitle(key) {
  return GAME_MODES.find((m) => m.key === key)?.title || key;
}

// For Match-mode entries the user-visible label is the format
// (Constructed / Peasant). Sandbox / Draft / Sealed keep the mode title.
function formatLabel(mode, format) {
  if (mode === 'match') return format === 'peasant' ? t('Peasant') : t('Constructed');
  return t(modeTitle(mode));
}

const VISIBILITY_LABELS = { solo: k('Solo'), friends: k('Friends'), public: k('Public') };

function visibilityLabel(v) {
  if (!v) return '';
  return VISIBILITY_LABELS[v] || v.charAt(0).toUpperCase() + v.slice(1);
}

function closesIn(inst, now) {
  if (inst.phase !== 'gathering' || inst.draft_event_id) return null;
  const base = new Date(inst.last_activity_at || inst.created_at).getTime();
  if (!Number.isFinite(base)) return null;
  const ttl = inst.visibility === 'public' ? PUBLIC_LOBBY_TTL_MS : LOBBY_TTL_MS;
  const left = base + ttl - now;
  if (left <= 0) return t('closing');
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  return h > 0 ? t('closes in {h}h {m}m', { h, m }) : t('closes in {m}m', { m: Math.max(1, m) });
}

// Glyph per activity kind. Small inline SVGs so the row reads at a glance
// without pulling in an icon set. `size` is the square side in cqw.
function KindGlyph({ kind, size = 1 }) {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { width: `${size}cqw`, height: `${size}cqw` } };
  if (kind === 'deck.comment' || kind === 'replay.comment' || kind === 'chat.reply' || kind === 'chat.mention') {
    return <svg {...common}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
  }
  if (kind === 'deck.like' || kind === 'deck.comment.like' || kind === 'replay.like' || kind === 'chat.like') {
    return <svg {...common}><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" /></svg>;
  }
  if (kind === 'chat.message') {
    return <svg {...common}><path d="M4 4h16v12H7l-3 3z" /><path d="M8 9h8M8 12h5" /></svg>;
  }
  if (kind === 'mail.received') {
    return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>;
  }
  if (kind === 'auction.bid') {
    return <svg {...common}><path d="M14 4l6 6-8 8-6-6z" /><path d="M3 21h9" /><path d="M11 12l-6 6" /></svg>;
  }
  if (kind === 'friend.request' || kind === 'friend.accepted') {
    return <svg {...common}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M19 8v6M16 11h6" /></svg>;
  }
  if (kind === 'invite' || kind === 'lobby') {
    // Crossed swords: a match waiting to happen.
    return (
      <svg {...common}>
        <path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
        <path d="M13 19l6-6" /><path d="M16 16l4 4" /><path d="M19 21l2-2" />
        <path d="M14.5 6.5L18 3h3v3l-3.5 3.5" />
        <path d="M5 14l4 4" /><path d="M7 17l-3 3" /><path d="M3 19l2 2" />
      </svg>
    );
  }
  return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></svg>;
}

// Leading visual for a row. A person's avatar when the event has a face
// behind it, with the kind glyph stamped bottom-right (cutout ring in the
// panel colour so it reads as sitting on top). Faceless events (a lobby
// closing, auction house mail) get the glyph alone on a disc.
// Solid stand-in for the panel gradient, for the cutout ring around the kind badge.
const PANEL_RING = "#151107";
const AVATAR_SIZE = 2.25;
function Avatar({ avatarId, name, kind, muted }) {
  const url = avatarId ? resolveAvatarUrl({ profileAvatar: avatarId }, getSorceryCards()) : null;
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const box = { width: `${AVATAR_SIZE}cqw`, height: `${AVATAR_SIZE}cqw` };
  const ring = { boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' };
  if (!name) {
    return (
      <div className="shrink-0 rounded-full flex items-center justify-center" style={{ ...box, ...ring, background: `${GOLD} 0.08)`, color: muted ? TEXT_MUTED : ACCENT_GOLD }}>
        <KindGlyph kind={kind} size={1} />
      </div>
    );
  }
  return (
    <div className="shrink-0 relative" style={box}>
      {url ? (
        <AvatarImage src={url} className="w-full h-full rounded-full" style={ring} />
      ) : (
        <div className="w-full h-full rounded-full flex items-center justify-center text-[0.875cqw] font-semibold" style={{ ...ring, background: `${GOLD} 0.14)`, color: TEXT_PRIMARY }}>
          {initial}
        </div>
      )}
      {kind ? (
        <span
          className="absolute rounded-full flex items-center justify-center"
          style={{
            right: '-0.2cqw', bottom: '-0.2cqw', width: '1cqw', height: '1cqw',
            background: '#1a1610', color: muted ? TEXT_MUTED : ACCENT_GOLD,
            boxShadow: `0 0 0 2px ${PANEL_RING}`,
          }}
        >
          <KindGlyph kind={kind} size={0.6} />
        </span>
      ) : null}
    </div>
  );
}

// Title copy with the actor's name set heavier than the rest, so the eye
// lands on who before what.
function RichTitle({ title, actor, strong }) {
  const weight = strong ? 600 : 400;
  if (actor && title.startsWith(actor)) {
    return (
      <span className="text-[0.75cqw] leading-snug" style={{ color: TEXT_BODY, fontWeight: weight }}>
        <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>{actor}</span>{title.slice(actor.length)}
      </span>
    );
  }
  return <span className="text-[0.75cqw] leading-snug" style={{ color: strong ? TEXT_PRIMARY : TEXT_BODY, fontWeight: weight }}>{title}</span>;
}

function Meta({ parts }) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div className="text-[0.625cqw] leading-snug mt-0.5 tabular-nums" style={{ color: TEXT_MUTED }}>
      {shown.map((p, i) => <span key={i}>{i > 0 ? ' · ' : ''}{p}</span>)}
    </div>
  );
}

// Same carved-stone material as the audio mixer popover: the texture stack
// over a fully opaque base, so nothing bleeds through a popover floating on
// a busy screen (see AudioMixerPopover.jsx for the reasoning).
const PANEL_STYLE = {
  ...TEXTURED_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, #0e0c08`,
};
// Rows lg (0.5cqw), padded wider than their radius so controls pick
// their own 0.25cqw; the pay popover (0.5cqw, 0.25cqw padding) → options 0.25cqw.
const ROW_RADIUS = 'var(--radius-lg)';
const ROW_PADDING_X = '0.625cqw';
const CONTROL_RADIUS = 'var(--radius-sm)';
const ACTION_BTN = 'vh-notif-btn py-1 px-3 text-[0.6875cqw] font-semibold cursor-pointer';
// Ghost buttons carry a transparent 1px border so they share the gold
// buttons' box height exactly; otherwise Decline sits 1px shorter than Accept.
const GHOST_BTN = 'vh-notif-ghost py-1 px-2 text-[0.6875cqw] cursor-pointer border border-transparent';

// One feed row: leading visual, content, trailing timestamp + unread dot.
// Flat, with a hairline between rows and a hover wash when clickable.
function Row({ leading, title, meta, body, actions, time, unread, clickable, dim, sound, onClick, children }) {
  return (
    <div
      className={`vh-notif-row flex items-start gap-2.5 py-2 ${clickable ? 'is-clickable cursor-pointer' : ''}`}
      style={{ opacity: dim ? 0.55 : 1, borderRadius: ROW_RADIUS, paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}
      data-sound={clickable ? sound : undefined}
      onClick={clickable ? onClick : undefined}
    >
      {leading}
      {/* Content gets the avatar's height as a floor and centres inside it:
          a one-liner sits level with the avatar, taller rows stay top-aligned. */}
      <div className="flex-1 min-w-0 flex flex-col justify-center" style={{ minHeight: `${AVATAR_SIZE}cqw` }}>
        <div className="flex items-baseline gap-2">
          <div className="flex-1 min-w-0">{title}</div>
          {time ? <span className="text-[0.5625cqw] shrink-0 tabular-nums" style={{ color: TEXT_MUTED }}>{time}</span> : null}
          {unread ? <span className="shrink-0 rounded-full self-center" style={{ width: '0.4cqw', height: '0.4cqw', background: ACCENT_GOLD }} /> : null}
        </div>
        {meta}
        {body ? <div className="text-[0.6875cqw] leading-snug mt-0.5 line-clamp-2" style={{ color: TEXT_BODY, opacity: 0.85 }}>{body}</div> : null}
        {children}
        {actions ? <div className="flex items-center gap-1.5 mt-1.5">{actions}</div> : null}
      </div>
    </div>
  );
}

function SectionLabel({ label, count }) {
  return (
    <div className="flex items-baseline gap-1.5 pt-2 pb-1 text-[0.5625cqw] uppercase tracking-[0.12em]" style={{ color: TEXT_MUTED, paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
      <span>{label}</span>
      {count > 0 ? <span className="tabular-nums font-semibold" style={{ color: ACCENT_GOLD }}>{count}</span> : null}
    </div>
  );
}

const ROW_MOTION = (i) => ({
  initial: { opacity: 0, y: -4 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.16, delay: Math.min(i, 8) * 0.03, ease: [0.16, 1, 0.3, 1] },
});

// Notifications tab of the social hub. Two sections: Matches (actionable,
// live rows from the instance system) above the divider, Activity (server
// feed merged with the inbox) below. `active` is true while the tab is on
// screen: the feed refetches, marks everything read and freezes which rows
// count as fresh so their dots survive until the player leaves the tab.
export default class NotificationsFeed extends Component {
  constructor(props) {
    super(props);
    this.state = {
      store: getState(),
      now: Date.now(),
      freshIds: new Set(),
      payMenuFor: null,
    };
  }

  componentDidMount() {
    this._unsub = subscribe((store) => this.setState({ store }));
    if (this.props.active) this.activate();
  }

  componentDidUpdate(prevProps) {
    if (this.props.active && !prevProps.active) this.activate();
    else if (!this.props.active && prevProps.active) this.deactivate();
  }

  componentWillUnmount() {
    try { this._unsub?.(); } catch {}
    clearInterval(this._tick);
  }

  activate() {
    // Rows that are unread right now keep their dot while the tab is
    // open, even though opening clears the badge server-side.
    const freshIds = new Set(
      this.state.store.activity.filter((a) => !a.readAt && !a.resolvedAt).map((a) => a.id),
    );
    this.setState({ freshIds, now: Date.now() });
    this._tick = setInterval(() => this.setState({ now: Date.now() }), 30_000);
    refreshMatches();
    refreshActivity().then(() => markAllRead());
  }

  deactivate() {
    clearInterval(this._tick);
    this._tick = null;
    this.setState({ freshIds: new Set(), payMenuFor: null });
  }

  // ── Matches actions ────────────────────────────────────────────────

  handleOpenInstance = (inst) => {
    openInstance(inst);
  };

  handleCancelInstance = async (inst, ev) => {
    ev.stopPropagation();
    const label = formatLabel(inst.mode, inst.format);
    const ok = await askConfirm({
      title: t('Cancel {label} lobby?', { label }),
      message: t('Everyone in this lobby will lose access.'),
      confirmLabel: t('Cancel lobby'),
      cancelLabel: t('Keep'),
      danger: true,
    });
    if (!ok) return;
    try {
      // /instances/:id/abandon cascades server-side into the linked
      // draft_event (abort + escrow refunds), so one call settles it.
      await apiAbandonInstance(inst.id);
      refreshMatches();
    } catch (err) {
      toast.error(err?.message || t('Could not cancel the lobby.'));
    }
  };

  handleLeaveInstance = async (inst, ev) => {
    ev.stopPropagation();
    const ok = await askConfirm({
      title: t('Leave lobby?'),
      message: t('Your seat opens up for someone else. You can rejoin later if you get invited again.'),
      confirmLabel: t('Leave'),
      cancelLabel: t('Stay'),
      danger: true,
    });
    if (!ok) return;
    try {
      // Draft/sealed seats leave via the draft event so the entry-fee
      // escrow is released; the plain instance /leave 409s those rows.
      const route = resolveDiscardLeave(inst, this.props.profile?.id);
      if (route.kind === 'leaveDraftEvent') await leaveDraftEvent(route.eventId);
      else await apiLeaveInstance(inst.id);
      refreshMatches();
    } catch (err) {
      toast.error(err?.message || t('Could not leave the lobby.'));
    }
  };

  cantAfford(inv) {
    if (inv.source !== 'draft') return false;
    const cost = Number(inv.metadata?.entryCost || 0);
    const coins = Number(this.props.profile?.coins || 0);
    const keystones = Number(this.props.profile?.keystones || 0);
    const ksCost = keystoneCostForMode(inv.metadata?.mode);
    return cost > 0 && cost > coins && keystones < ksCost;
  }

  // Paid draft/sealed invites: the Accept button carries the price and a
  // chevron that opens a pay-with picker (Keystone vs gold). `paymentMethod`
  // rides through the bus into openDraftLobby's join call.
  togglePayMenu = (inv, ev) => {
    ev.stopPropagation();
    this.setState({ payMenuFor: this.state.payMenuFor === inv.id ? null : inv.id });
  };

  handleAcceptInvite = (inv, paymentMethod = null) => {
    if (this.cantAfford(inv)) {
      const cost = Number(inv.metadata?.entryCost || 0);
      const ksCost = keystoneCostForMode(inv.metadata?.mode);
      toast.error(ksCost > 1
        ? t('Not enough. This costs {cost} coins or {n} Keystones.', { cost, n: ksCost })
        : t('Not enough. This costs {cost} coins or {n} Keystone.', { cost, n: ksCost }));
      return;
    }
    removeInviteLocally(inv.id);
    if (inv.source === 'draft-match') {
      acceptDraftMatchInvite(inv.eventId, inv.matchId);
    } else if (inv.source === 'instance') {
      acceptInstanceInvite(inv.senderId, inv);
    } else if (inv.source === 'draft') {
      acceptDraftInvite(inv.eventId, inv, paymentMethod ? { paymentMethod } : undefined);
    }
  };

  handleDeclineInvite = (inv) => {
    removeInviteLocally(inv.id);
    if (inv.source === 'draft-match') {
      dismissDraftMatchInvite(inv.matchId).catch(() => {});
    } else if (inv.source === 'instance') {
      declineInstanceInvite(inv.instanceId).catch(() => {});
    } else if (inv.source === 'draft') {
      // Removes the draft_invites row and, if nobody is left, refunds the
      // creator via abortDraft.
      declineDraftEvent(inv.eventId).catch(() => {});
    }
  };

  // ── Activity actions ───────────────────────────────────────────────

  handleOpenActivity = (n) => {
    if (!notificationHasTarget(n)) return;
    openNotification(n);
  };

  handleFriendRequest = async (n, accept, ev) => {
    ev.stopPropagation();
    const senderId = n.actorId;
    resolveNotification(n.id);
    try {
      await (accept ? acceptFriendRequest(senderId) : declineFriendRequest(senderId));
    } catch (err) {
      // 404: already handled elsewhere (other device, reciprocal add).
      const msg = err?.message || '';
      if (!/not found/i.test(msg) && !/404/.test(msg)) toast.error(msg || t('Could not update the request.'));
    }
    refreshFriendList().catch(() => {});
  };

  // ── Render ─────────────────────────────────────────────────────────

  renderInvite(inv, i) {
    const senderLabel = inv.senderName || inv.senderId;
    const isDraft = inv.source === 'draft';
    const isDraftMatch = inv.source === 'draft-match';
    const m = inv.metadata || {};
    const label = formatLabel(m.mode, m.format);
    const draftKsCost = keystoneCostForMode(m.mode);
    const cantAfford = this.cantAfford(inv);
    const cost = Number(m.entryCost || 0);
    const coins = Number(this.props.profile?.coins || 0);
    const keystones = Number(this.props.profile?.keystones || 0);

    let title;
    if (isDraftMatch) title = m.mode === 'sealed' ? t('Your Sealed match is ready') : t('Your Draft match is ready');
    else if (m.resume) title = t('{name} wants to resume your {label} match', { name: senderLabel, label });
    else title = isDraft
      ? t('{name} invited you to a {label} event', { name: senderLabel, label })
      : t('{name} invited you to a {label} match', { name: senderLabel, label });

    const meta = [
      isDraftMatch && m.round ? t('Round {n}', { n: m.round }) : null,
      isDraftMatch ? t('vs {name}', { name: senderLabel }) : null,
      m.visibility ? t(visibilityLabel(m.visibility)) : (isDraft ? t('Private') : null),
      stakesLabel(m.mode, m.rankedOptIn),
      isDraft ? (m.setLabel || (m.setKey ? m.setKey.charAt(0).toUpperCase() + m.setKey.slice(1) : null)) : null,
      isDraft && m.podSize ? (m.participantCount != null ? t('{count}/{size} players', { count: m.participantCount, size: m.podSize }) : t('{n} players', { n: m.podSize })) : null,
    ];
    const paid = isDraft && cost > 0;
    const canKeystone = keystones >= draftKsCost;
    const canCoins = coins >= cost;
    const defaultPay = canKeystone ? 'keystone' : 'coins';
    const payMenuOpen = this.state.payMenuFor === inv.id;
    const ksLabel = draftKsCost > 1 ? t('{n} Keystones', { n: draftKsCost }) : t('{n} Keystone', { n: draftKsCost });
    const priceTag = (method) => method === 'keystone'
      ? <span className="inline-flex items-center gap-0.5 tabular-nums">{draftKsCost}<KeystoneIcon size={11} /></span>
      : <span className="inline-flex items-center gap-0.5 tabular-nums">{cost}<CoinIcon size={11} /></span>;

    return (
      <motion.div key={inv.id} {...ROW_MOTION(i)}>
        <Row
          leading={<Avatar avatarId={inv.senderAvatar} name={senderLabel} kind="invite" />}
          title={<RichTitle title={title} actor={isDraftMatch ? null : senderLabel} strong />}
          meta={<Meta parts={[...meta, paid ? t('{gold} gold or {keystones}', { gold: cost.toLocaleString(), keystones: ksLabel }) : null]} />}
          time={timeAgo(inv.createdAt)}
          actions={(
            <>
              {paid ? (
                <div className="relative flex items-stretch" style={{ opacity: cantAfford ? 0.5 : 1 }}>
                  <button
                    type="button"
                    className={`${ACTION_BTN} inline-flex items-center gap-1.5 whitespace-nowrap`}
                    style={{ ...GOLD_BTN, borderRadius: `${CONTROL_RADIUS} 0 0 ${CONTROL_RADIUS}` }}
                    data-sound={UI.CONFIRM}
                    disabled={cantAfford}
                    title={defaultPay === 'keystone' ? t('Accept and spend {keystones}', { keystones: ksLabel }) : t('Accept and pay {cost} gold', { cost })}
                    onClick={() => this.handleAcceptInvite(inv, defaultPay)}
                  >
                    <span>{t('Accept')}</span>
                    <span style={{ opacity: 0.75 }}>·</span>
                    {priceTag(defaultPay)}
                  </button>
                  <button
                    type="button"
                    className="vh-notif-btn px-1.5 flex items-center cursor-pointer"
                    style={{ ...GOLD_BTN, borderRadius: `0 ${CONTROL_RADIUS} ${CONTROL_RADIUS} 0`, borderLeft: '1px solid rgba(90,65,20,0.45)' }}
                    data-sound={UI.SELECT}
                    disabled={cantAfford}
                    title={t('Choose how to pay')}
                    aria-label={t('Choose how to pay')}
                    aria-expanded={payMenuOpen}
                    onClick={(e) => this.togglePayMenu(inv, e)}
                  >
                    <IconChevronDown style={{ width: '0.75cqw', height: '0.75cqw', transform: payMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
                  </button>
                  {payMenuOpen ? (
                    <div
                      className="absolute left-0 z-10 flex flex-col"
                      style={{ top: 'calc(100% + 0.25cqw)', padding: CONTROL_RADIUS, ...PANEL_STYLE, borderRadius: 'var(--radius-lg)', boxShadow: '0 12px 40px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(180,140,60,0.2)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {[
                        { method: 'keystone', icon: <KeystoneIcon size={12} />, label: ksLabel, have: keystones, ok: canKeystone },
                        { method: 'coins', icon: <CoinIcon size={12} />, label: t('{n} Gold', { n: cost.toLocaleString() }), have: coins, ok: canCoins },
                      ].map((opt) => (
                        <button
                          key={opt.method}
                          type="button"
                          className="vh-notif-ghost flex items-center gap-2 pl-2 pr-2.5 py-1.5 whitespace-nowrap cursor-pointer"
                          style={{ color: opt.ok ? TEXT_PRIMARY : TEXT_MUTED, borderRadius: CONTROL_RADIUS, opacity: opt.ok ? 1 : 0.5 }}
                          disabled={!opt.ok}
                          data-sound={UI.CONFIRM}
                          onClick={() => { this.setState({ payMenuFor: null }); this.handleAcceptInvite(inv, opt.method); }}
                        >
                          <span className="flex items-center justify-center shrink-0" style={{ width: '1cqw', color: opt.ok ? ACCENT_GOLD : TEXT_MUTED }}>{opt.icon}</span>
                          <span className="text-[0.6875cqw] font-semibold">{opt.label}</span>
                          <span className="ml-auto pl-4 text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>{t('{n} owned', { n: opt.have.toLocaleString() })}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  className={ACTION_BTN}
                  style={{ ...GOLD_BTN, borderRadius: CONTROL_RADIUS }}
                  data-sound={UI.CONFIRM}
                  onClick={() => this.handleAcceptInvite(inv)}
                >
                  {isDraftMatch ? t('Join') : t('Accept')}
                </button>
              )}
              <button
                type="button"
                className={GHOST_BTN}
                style={{ color: TEXT_MUTED, borderRadius: CONTROL_RADIUS }}
                data-sound={UI.CANCEL}
                onClick={() => this.handleDeclineInvite(inv)}
              >
                {isDraftMatch ? t('Not now') : t('Decline')}
              </button>
            </>
          )}
        >
          {cantAfford ? (
            <div className="text-[0.625cqw] mt-0.5" style={{ color: '#c45050' }}>
              {draftKsCost > 1
                ? t('Need {cost} coins or {n} Keystones (you have {coins} / {keystones}).', { cost, n: draftKsCost, coins, keystones })
                : t('Need {cost} coins or {n} Keystone (you have {coins} / {keystones}).', { cost, n: draftKsCost, coins, keystones })}
            </div>
          ) : null}
        </Row>
      </motion.div>
    );
  }

  renderInstance(inst, i) {
    const myId = this.props.profile?.id;
    const me = (inst.participants || []).find((p) => p.userId === myId);
    const isCreator = me?.role === 'creator';
    const count = (inst.participants || []).length;
    const needed = Math.max(0, (inst.seats_min || 2) - count);
    const waitingOnResume = inst.phase === 'paused';
    const ttl = closesIn(inst, this.state.now);
    const stakes = stakesLabel(inst.mode, inst.ranked_opt_in);
    const title = isCreator
      ? t('Your {visibility} {format} lobby', { visibility: visibilityLabel(inst.visibility).toLowerCase(), format: formatLabel(inst.mode, inst.format) })
      : t('In a {visibility} {format} lobby', { visibility: visibilityLabel(inst.visibility).toLowerCase(), format: formatLabel(inst.mode, inst.format) });
    const status = waitingOnResume
      ? t('Waiting for the other player to accept the resume')
      : needed > 0 ? t('Waiting for {n} more', { n: needed }) : t('Ready {dash} waiting for ready-check', { dash: EM_DASH });
    const action = isCreator
      ? { label: t('Cancel'), onClick: (e) => this.handleCancelInstance(inst, e) }
      : inst.phase === 'gathering' ? { label: t('Leave'), onClick: (e) => this.handleLeaveInstance(inst, e) } : null;
    return (
      <motion.div key={inst.id} {...ROW_MOTION(i)}>
        <Row
          leading={<Avatar kind="lobby" />}
          title={<RichTitle title={title} strong />}
          meta={<Meta parts={[status, inst.seats_max > 2 ? `${count}/${inst.seats_max}` : null, stakes, ttl]} />}
          clickable
          sound={UI.HOVER}
          onClick={() => this.handleOpenInstance(inst)}
          actions={action ? (
            <button type="button" className={`${GHOST_BTN} is-danger`} style={{ color: '#c45050', marginLeft: '-0.5cqw', borderRadius: CONTROL_RADIUS }} data-sound={UI.CANCEL} onClick={action.onClick}>
              {action.label}
            </button>
          ) : null}
        />
      </motion.div>
    );
  }

  // Avatar for a server feed row: stamped on the payload by the server for
  // events with an actor; the friend list fills in for rows written before
  // that field existed.
  activityAvatar(n) {
    const p = n.payload || {};
    if (p.actorAvatar || p.senderAvatar) return p.actorAvatar || p.senderAvatar;
    if (!n.actorId) return null;
    const friend = (getCachedFriendList().friends || []).find((f) => f.id === n.actorId);
    return friend?.profileAvatar || null;
  }

  renderActivity(n, i) {
    const { title, body, actor } = describeNotification(n);
    const fresh = this.state.freshIds.has(n.id) || (!n.readAt && !n.resolvedAt);
    const clickable = notificationHasTarget(n);
    const isRequest = n.kind === 'friend.request' && !n.resolvedAt;
    const many = Number(n.payload?.count) > 1;
    const hasFace = !!actor && !!n.actorId && !(many && n.kind.endsWith('.like'));
    return (
      <motion.div key={n.id} {...ROW_MOTION(i)}>
        <Row
          leading={<Avatar avatarId={hasFace ? this.activityAvatar(n) : null} name={hasFace ? actor : null} kind={n.kind} muted={!fresh} />}
          title={<RichTitle title={title} actor={hasFace ? actor : null} strong={fresh} />}
          body={body}
          time={timeAgo(n.createdAt)}
          unread={fresh}
          clickable={clickable}
          dim={!!n.resolvedAt}
          sound={UI.HOVER}
          onClick={() => this.handleOpenActivity(n)}
          actions={isRequest ? (
            <>
              <button type="button" className={ACTION_BTN} style={{ ...GOLD_BTN, borderRadius: CONTROL_RADIUS }} data-sound={UI.CONFIRM} onClick={(e) => this.handleFriendRequest(n, true, e)}>
                {t('Accept')}
              </button>
              <button type="button" className={GHOST_BTN} style={{ color: TEXT_MUTED, borderRadius: CONTROL_RADIUS }} data-sound={UI.CANCEL} onClick={(e) => this.handleFriendRequest(n, false, e)}>
                {t('Decline')}
              </button>
            </>
          ) : null}
        />
      </motion.div>
    );
  }

  // Inbox row (auction payout, news, season reward). Stands in for its
  // mail.received notification: the inbox knows about claims and
  // deletions, the feed row does not.
  renderMail(m, i) {
    const cards = m.attachedCards || m.cards || [];
    const coins = m.attachedCoins || m.coins || 0;
    const hasAttachments = cards.length > 0 || coins > 0;
    const { title, body } = describeNotification({
      kind: 'mail.received',
      payload: { mailType: m.type, senderName: m.senderName, subject: m.subject, hasAttachments: hasAttachments && !m.claimed },
    });
    const fresh = !m.readAt;
    return (
      <motion.div key={`mail-${m.id}`} {...ROW_MOTION(i)}>
        <Row
          leading={<Avatar kind="mail.received" muted={!fresh} />}
          title={<RichTitle title={title} strong={fresh} />}
          meta={coins > 0 && !m.claimed ? (
            <Meta parts={[<span className="inline-flex items-center gap-1 font-bold" style={{ color: COIN_COLOR }}><CoinIcon size={10} />{coins}</span>]} />
          ) : null}
          body={body}
          time={timeAgo(m.createdAt)}
          unread={fresh}
          clickable
          sound={UI.HOVER}
          onClick={() => this.props.onOpenMail?.(m)}
        />
      </motion.div>
    );
  }

  render() {
    const { store } = this.state;
    const { invites, instances, activity, matchCount } = store;
    const feed = [
      ...activity
        .filter((n) => n.kind !== 'mail.received' && (!n.resolvedAt || n.kind !== 'friend.request'))
        .map((n) => ({ at: n.createdAt, render: (i) => this.renderActivity(n, i) })),
      ...(this.props.mail || []).map((m) => ({ at: m.createdAt, render: (i) => this.renderMail(m, i) })),
    ].sort((a, b) => new Date(b.at) - new Date(a.at));

    return (
      <div className="flex flex-col" onClick={() => { if (this.state.payMenuFor) this.setState({ payMenuFor: null }); }}>
        {matchCount > 0 ? (
          <div className="flex flex-col gap-[0.125cqw]">
            <SectionLabel label={t('Matches')} count={matchCount} />
            {invites.map((inv, i) => this.renderInvite(inv, i))}
            {instances.map((inst, i) => this.renderInstance(inst, invites.length + i))}
          </div>
        ) : null}

        {feed.length > 0 ? (
          <div className="flex flex-col gap-[0.125cqw]" style={matchCount > 0 ? { marginTop: '0.5cqw' } : null}>
            <SectionLabel label={t('Activity')} />
            {feed.map((f, i) => f.render(matchCount + i))}
          </div>
        ) : null}

        {matchCount === 0 && feed.length === 0 ? (
          <div className="text-center px-6 py-8 text-[0.75cqw] text-pretty" style={{ color: TEXT_MUTED }}>
            {store.loading ? t('Loading...') : t('Nothing new. Invites, mail, bids and news land here.')}
          </div>
        ) : null}
      </div>
    );
  }
}
